import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { Spark, SparkDisplayStatus, accountCanCreateSpark } from '@sparkplug/lib';

import { SparkDashboardContent } from '@features/spark-dashboard/components/SparkDashboardContent';

import {
    useSparkManagementModal,
    wrapWithSparkManagementModal,
} from '@contexts/SparkManagementModalContext';

import { NoInboxSparksGraphic, NoSparksGraphic } from '@components/graphics';
import { ViewList, ViewModule } from '@components/icons';
import AppContentWithSidebar from '@components/layout/AppContentWithSidebar';
import EmptyStateDisplay from '@components/layout/EmptyStateDisplay';
import PageHeader from '@components/layout/PageHeader';
import PageLoading from '@components/layout/PageLoading';
import UpgradeModal from '@components/overlays/UpgradeModal';
import CreateSparkButton from '@components/sparks/CreateSparkButton';
import Toolbar from '@components/toolbar/Toolbar';

import { useApp, useAppBodyClasses, withMobileView } from '@hooks/AppHooks';
import { useSparkplugAccount } from '@hooks/SparkplugAccountsHooks';
import { useSparks } from '@hooks/SparksHooks';
import { useSearch } from '@hooks/UIHooks';

import { applyFilterFns } from '@helpers/ui';

import { IBrandLink } from '@app/types/BrandLinksTypes';
import { IPosLocation } from '@app/types/PosTypes';
import { IOption, IOptionTree } from '@app/types/UITypes';

import SparksViewMobile from '../SparksViewMobile';
import SparkPickerModal from './SparkPickerModal';
import SparkStatusButtons from './SparkStatusButtons';
import {
    SPARK_SCHEDULE_OPTIONS,
    SPARK_TRAINING_OPTIONS,
    SPARK_TYPE_OPTIONS,
} from './SparksView.constants';
import { useSparksFilters } from './useSparkFilters';

import './SparksView.scss';

const SparksViewModeKey = 'sparkplug::sparks::viewMode';

interface ILocationOptions {
    optionTree: IOptionTree<{}>[];
    allLocations: IOption<{ key: string }>[];
}

const getLocationOptionsForBrands = (brandLinks: IBrandLink[] = []): ILocationOptions => {
    const formatLocations = (locations: IPosLocation[]) =>
        locations.map((location) => ({
            label: location.displayName ?? location.name,
            value: location._id,
            key: location._id,
        }));

    let optionTree: IOptionTree<{}>[] = [];
    let allLocations: IOption<{ key: string }>[] = [];
    if (brandLinks.length) {
        optionTree = brandLinks
            .filter(({ locations }) => locations?.length)
            .map(({ label, retailerAccountId, locations = [] }) => {
                const formattedLocations = formatLocations(locations);
                allLocations = allLocations.concat(formattedLocations);

                return {
                    label,
                    value: retailerAccountId,
                    children: formattedLocations,
                };
            });
    }

    return {
        optionTree,
        allLocations,
    };
};

const getLocationOptionsForRetailers = (
    accountLocations: IOption<IPosLocation>[] = [],
): ILocationOptions => {
    return {
        optionTree: accountLocations,
        allLocations: accountLocations.map((location) => ({ ...location, key: location._id })),
    };
};

const SparkViewModes = [
    {
        value: 'list',
        label: <ViewList />,
    },
    {
        value: 'grid',
        label: <ViewModule />,
    },
];

export type SparkViewProps = {
    showPicker?: boolean;
    sparkPickerType?: 'leaderboard' | 'goal' | 'commission' | null;
    forceSparkApprovalModal?: {
        sparkId: string;
    };
    initialSparkStatus?: SparkDisplayStatus;
};

export const SparksView = wrapWithSparkManagementModal(
    ({
        showPicker = false,
        forceSparkApprovalModal,
        initialSparkStatus = 'active',
    }: SparkViewProps) => {
        const { history, userIsAdmin, user } = useApp();

        const { sparks, sparksAreReady, refetchSparks } = useSparks();
        const { openCreateModal, openApprovalModal } = useSparkManagementModal();

        const { account, connectEnabled } = useSparkplugAccount();
        useAppBodyClasses(['dashboard-app-content'], []);

        const { sparkFilters, updateSparkFilters, applySparkFilters } = useSparksFilters<Spark>({
            accountType: account?.type,
            initialFilters: {
                location: [],
                type: [],
                status: initialSparkStatus,
                schedule: [],
                training: [],
            },
        });

        const { searchFilter, onChangeSearchFilter, applySearch } = useSearch([
            'name',
            'description',
            account?.type === 'brand' ? 'sparkRetailer.name' : 'sparkVendor.name',
            'sparkBrand.name',
            'sparkCreator.fullName',
        ]);

        const [viewMode, setViewMode] = useState(localStorage.getItem(SparksViewModeKey) || 'grid');
        const [showSparkPickerModal, setShowSparkPickerModal] = useState(showPicker);
        const [upgradeModalIsVisible, setUpgradeModalIsVisible] = useState(false);

        const pendingSparkCount = useMemo(() => {
            return (sparks || []).filter(({ requestState = '' }) => {
                if (requestState === 'pending') {
                    return true;
                }
                return false;
            }).length;
        }, [sparks, account?.type]);

        const locationOptions: ILocationOptions = useMemo(() => {
            if (account?.type === 'brand') {
                return getLocationOptionsForBrands(account?.brandLinks);
            } else {
                // we want to be able to view "complete" sparks at all locations - even if that location has since been disabled
                if (sparkFilters.status === 'complete') {
                    // this is ugly, but because we are now showing complete sparks for disabled locations, we need to include that location in the "Locations" filter drop down when on the complete tab.  But, because disabled=true on those locations, the option on the dropdown also becomes disabled - and cannot be altered in that state.  But, if we alter the "disabled" prop directly on the locations array, we alter it on the core object itself, effectively breaking any display of "disabled" for that location anywhere else.  Adding a custom prop to the object allows us to keep the option enabled without altering the actual "disabled" prop on the location object itself.
                    const locationOptionTree = getLocationOptionsForRetailers(
                        account?.allLocations,
                    );
                    locationOptionTree.optionTree.forEach((locationOption: any) => {
                        if (locationOption.disabled) {
                            // eslint-disable-next-line no-param-reassign
                            locationOption.keepOptionEnabled = true;
                        }
                    });
                    return locationOptionTree;
                } else {
                    return getLocationOptionsForRetailers(account?.locations);
                }
            }
        }, [account, sparkFilters.status]);

        const onClickCreateSparkButton = () => setShowSparkPickerModal(true);

        const onSparkCardSelected = useCallback(
            (spark: Pick<Spark, '_id' | 'requestState'>) => {
                if (spark?.requestState === 'pending' && account?.type !== 'brand') {
                    openApprovalModal(spark._id);
                } else {
                    history.push(`/${account?._id}/sparks/${spark._id}`, {
                        prevUrl: document.location.pathname,
                    });
                }
            },
            [account, openApprovalModal],
        );

        useEffect(() => {
            refetchSparks();
        }, []);

        useEffect(() => {
            if (forceSparkApprovalModal?.sparkId) {
                openApprovalModal(forceSparkApprovalModal?.sparkId);
            }
        }, [forceSparkApprovalModal]);

        useEffect(() => {
            updateSparkFilters({ location: locationOptions?.allLocations });
        }, [locationOptions]);

        const filteredSparks = useMemo(
            () => applyFilterFns<Spark>([applySparkFilters, applySearch], sparks),
            [applySparkFilters, applySearch, sparks],
        );

        useEffect(() => {
            if (showPicker && user && account) {
                const canCreateSpark = accountCanCreateSpark(account);

                if (!canCreateSpark) {
                    setShowSparkPickerModal(false);
                    setUpgradeModalIsVisible(true);
                }
            }
        }, [showPicker, account, user]);

        let emptyStateComponent: ReactElement | null = null;
        const isInboxOrSent = sparkFilters.status === 'inbox/sent';
        if (isInboxOrSent && account?.type === 'retailer') {
            emptyStateComponent = (
                <EmptyStateDisplay graphic={<NoInboxSparksGraphic />} label="no new spark offers" />
            );
        } else {
            emptyStateComponent = (
                <EmptyStateDisplay
                    graphic={<NoSparksGraphic />}
                    label={
                        isInboxOrSent ? 'no sparks found' : `no ${sparkFilters.status} sparks found`
                    }
                    actionButton={<CreateSparkButton onClick={onClickCreateSparkButton} />}
                />
            );
        }

        return (
            <div className="sparks-view">
                <PageHeader title={`${account?.name || ''} Sparks`.trim()}>
                    <Toolbar collapseOnMobile>
                        <Toolbar.Group className="toolbar-group-end">
                            <CreateSparkButton onClick={onClickCreateSparkButton} />
                        </Toolbar.Group>
                    </Toolbar>
                </PageHeader>

                <Toolbar className="spark-view__status-toolbar" scrollOnMobile>
                    <SparkStatusButtons
                        value={sparkFilters.status}
                        onSelect={({ value, to }) => {
                            window.history.pushState(null, '', `/${account?._id}${to}`);
                            updateSparkFilters({ status: value });
                        }}
                        isBrand={account?.type === 'brand'}
                        inboxCount={pendingSparkCount}
                        isAdmin={userIsAdmin}
                        connectEnabled={connectEnabled}
                    />
                </Toolbar>

                <Toolbar className="spark-view__filter-toolbar" scrollOnMobile>
                    <Toolbar.DropdownListSelector
                        label={account?.type === 'brand' ? 'Retailer Locations' : 'Locations'}
                        selected={sparkFilters.location || locationOptions.allLocations}
                        options={locationOptions.optionTree}
                        onSelectionChanged={(v) => {
                            updateSparkFilters({ location: v });
                        }}
                        clear={{
                            active:
                                (sparkFilters.location || []).length !==
                                locationOptions.allLocations.length,

                            onClear: () => {
                                updateSparkFilters({
                                    location: locationOptions.allLocations,
                                });
                            },
                        }}
                    />

                    <Toolbar.Search
                        name="sparks-search"
                        defaultValue={searchFilter}
                        onChange={onChangeSearchFilter}
                    />

                    <Toolbar.MultiSelectDropdown
                        label="Types"
                        options={SPARK_TYPE_OPTIONS}
                        selected={sparkFilters.type}
                        onApply={(value) => updateSparkFilters({ type: value })}
                    />

                    <Toolbar.MultiSelectDropdown
                        label="Schedules"
                        options={SPARK_SCHEDULE_OPTIONS}
                        selected={sparkFilters.schedule}
                        onApply={(value) => updateSparkFilters({ schedule: value })}
                    />

                    {(account?.zoltrainEnabled || account?.seedTalentEnabled) && (
                        <Toolbar.MultiSelectDropdown
                            label="Training"
                            options={SPARK_TRAINING_OPTIONS}
                            selected={sparkFilters.training}
                            onApply={(value) => updateSparkFilters({ training: value })}
                        />
                    )}

                    <div className="column-widget-placeholder" />
                    <div className="export-to-csv-placeholder" />
                </Toolbar>

                {sparksAreReady ? (
                    <AppContentWithSidebar
                        sidebar={
                            <SparkStatusButtons
                                value={sparkFilters.status}
                                onSelect={({ value, to }) => {
                                    window.history.pushState(null, '', `/${account?._id}${to}`);
                                    updateSparkFilters({ status: value });
                                }}
                                isBrand={account?.type === 'brand'}
                                inboxCount={pendingSparkCount}
                                isAdmin={userIsAdmin}
                                connectEnabled={connectEnabled}
                            />
                        }
                        content={
                            <SparkDashboardContent
                                sparkFilters={sparkFilters}
                                emptyStateComponent={emptyStateComponent}
                                sparks={filteredSparks}
                                onSparkSelected={onSparkCardSelected}
                            />
                        }
                    />
                ) : (
                    <PageLoading label="Loading Sparks..." />
                )}

                <SparkPickerModal
                    isVisible={showSparkPickerModal}
                    onClose={() => setShowSparkPickerModal(false)}
                    onNewSpark={(selected) => {
                        setShowSparkPickerModal(false);
                        openCreateModal(selected, 'spark-dashboard');
                    }}
                />

                <UpgradeModal
                    isVisible={upgradeModalIsVisible}
                    onClose={() => setUpgradeModalIsVisible(false)}
                />
            </div>
        );
    },
);

export default withMobileView(SparksView, SparksViewMobile, ({ userIsAdmin }) => {
    return !userIsAdmin;
});
